import { siteClock } from 'src/app/shared/site-time';
import { closePopup, commit, currentPopup, el, ICONS, openPopup, Popup } from './popup';

// Date, date-and-time, month and time fields open our own calendar and clock in the theme's colours instead of the
// browser's (the owner, 2026-10-30). The <input> stays the field: typing into it works as before and the panel follows
// what is typed; a pick writes the value in the input's own format and sends input + change, so ngModel, formControlName
// and (change) all see it. Month and day names come from the page's language (Intl), the digits too - Bangla pages get
// Bangla digits, as their dates do everywhere else - and the week starts where that language starts it.
//
// Left to the browser: a disabled or read-only field, one marked data-native (or inside [data-native-pickers]), and
// touch presses (a phone's own wheels). Keyboard: Alt+Down or F4 in the field opens it with the focus in the calendar;
// arrows move a day (a week up and down), Page Up/Down a month, Home/End the week, Enter picks, Escape leaves.

type Kind = 'date' | 'datetime-local' | 'month' | 'time';
type View = 'days' | 'months' | 'years';
const KINDS = ['date', 'datetime-local', 'month', 'time'];
const YEARS_PAGE = 24;

interface Ymd { y: number; m: number; d: number; }
interface Hm { h: number; mi: number; }

const pad = (n: number, len = 2) => String(n).padStart(len, '0');
const isoDay = (v: Ymd) => pad(v.y, 4) + '-' + pad(v.m + 1) + '-' + pad(v.d);
const isoMonth = (y: number, m: number) => pad(y, 4) + '-' + pad(m + 1);
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

function parseDay(s: string): Ymd | null {
    const m = /^(\d{4,})-(\d{2})(?:-(\d{2}))?/.exec(s || '');
    return m ? { y: +m[1], m: +m[2] - 1, d: m[3] ? +m[3] : 1 } : null;
}

function parseTime(s: string): Hm | null {
    const m = /(?:^|T)(\d{2}):(\d{2})/.exec(s || '');
    return m ? { h: +m[1], mi: +m[2] } : null;
}

function addDays(v: Ymd, n: number): Ymd {
    const d = new Date(Date.UTC(v.y, v.m, v.d + n));
    return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
}

function addMonths(v: Ymd, n: number): Ymd {
    const total = v.y * 12 + v.m + n;
    const y = Math.floor(total / 12);
    const m = total - y * 12;
    return { y, m, d: Math.min(v.d, daysIn(y, m)) };
}

function today(win: Window): Ymd {
    const d = siteClock();   // the Bangladesh day (shared/site-time)
    return { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() };
}

/** The language's first day of the week, 0 = Sunday (Intl.Locale's week info where the browser has it). */
function firstDayOf(lang: string): number {
    try {
        const L = (Intl as any).Locale;
        if (L) {
            const loc = new L(lang);
            const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
            if (info && info.firstDay) {
                return info.firstDay % 7;
            }
        }
    } catch { }
    return 0;
}

export function installDatePicker(doc: Document, isTouch: () => boolean, t: (key: string, fallback: string) => string): void {
    const win = doc.defaultView as Window;
    let input: HTMLInputElement | null = null;
    let popup: Popup | null = null;
    let panel: HTMLElement | null = null;
    let kind: Kind = 'date';
    let view: View = 'days';
    let cursor: Ymd = today(win);   // the month on show, and the day the keyboard is on
    let yearsFrom = 0;
    let keyFocus = false;           // the focus is in the panel (opened from the keyboard)

    const skip = (i: EventTarget | null): boolean =>
        !(i instanceof HTMLInputElement) || KINDS.indexOf(i.type) < 0 || i.disabled || i.readOnly ||
        i.hasAttribute('data-native') || !!i.closest('[data-native-pickers]');

    const isOpenFor = (i: HTMLInputElement) => !!popup && input === i && currentPopup() === popup;

    // A press on the field's icon (its last 34 px) keeps the browser's own picker shut and opens ours; a press on the
    // text opens ours beside the typing, which still works.
    doc.addEventListener('mousedown', e => {
        const i = e.target as HTMLInputElement;
        if (skip(i) || e.button !== 0 || isTouch()) {
            return;
        }
        const r = i.getBoundingClientRect();
        const rtl = win.getComputedStyle(i).direction === 'rtl';
        const onIcon = rtl ? e.clientX < r.left + 34 : e.clientX > r.right - 34;
        if (onIcon) {
            e.preventDefault();
            i.focus({ preventScroll: true });
        }
        if (isOpenFor(i)) {
            if (onIcon) {
                closePopup();
            }
            return;
        }
        open(i, false);
    }, true);

    // The click is what opens the browser's picker (Firefox: anywhere on the field; Chrome: on the icon).
    doc.addEventListener('click', e => {
        if (!skip(e.target) && !isTouch()) {
            e.preventDefault();
        }
    }, true);

    doc.addEventListener('keydown', e => {
        if (popup && input && currentPopup() === popup) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                const i = input;
                closePopup();
                i.focus({ preventScroll: true });
                return;
            }
            if (panel && panel.contains(e.target as Node)) {
                onPanelKey(e);
                return;
            }
        }
        const i = e.target as HTMLInputElement;
        if (skip(i)) {
            return;
        }
        if (e.key === 'F4' || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp'))) {
            e.preventDefault();
            if (isOpenFor(i)) {
                closePopup();
            } else {
                open(i, true);
            }
        }
    }, true);

    // Typing in the field: the panel shows what was typed.
    doc.addEventListener('input', e => {
        if (input && e.target === input && popup && currentPopup() === popup && !writing) {
            readValue();
            render();
        }
    }, true);

    // The focus moving on to anything else (Tab) closes it.
    doc.addEventListener('focusin', e => {
        if (popup && currentPopup() === popup && panel && input && e.target !== input && !panel.contains(e.target as Node)) {
            closePopup();
        }
    }, true);

    let writing = false;

    function open(i: HTMLInputElement, byKey: boolean) {
        input = i;
        kind = i.type as Kind;
        keyFocus = byKey;
        readValue();
        view = kind === 'month' ? 'months' : 'days';
        panel = el(doc, 'div', 'ph-dp ph-dp--' + kind);
        panel.setAttribute('role', 'dialog');
        panel.addEventListener('mousedown', e => e.preventDefault());
        panel.addEventListener('click', onPanelClick);
        const owner = i;
        i.setAttribute('aria-expanded', 'true');
        render(false);
        popup = openPopup(doc, i, panel, {
            onClose: () => {
                owner.removeAttribute('aria-expanded');
                if (input === owner) {
                    input = null;
                    popup = null;
                    panel = null;
                }
            },
        });
        afterRender();
    }

    function readValue() {
        if (!input) {
            return;
        }
        const day = parseDay(input.value);
        cursor = day || clampToRange(today(win));
        yearsFrom = cursor.y - (cursor.y % YEARS_PAGE);
    }

    function clampToRange(v: Ymd): Ymd {
        const lo = input && parseDay(input.min);
        const hi = input && parseDay(input.max);
        const s = isoDay(v);
        if (lo && s < isoDay(lo)) {
            return lo;
        }
        if (hi && s > isoDay(hi)) {
            return hi;
        }
        return v;
    }

    const lang = () => doc.documentElement.lang || 'en';
    const num = (n: number, digits = 1) => {
        try {
            return new Intl.NumberFormat(lang(), { minimumIntegerDigits: digits, useGrouping: false }).format(n);
        } catch {
            return pad(n, digits);
        }
    };
    const fmt = (opts: Intl.DateTimeFormatOptions, y: number, m: number, d = 1) => {
        try {
            return new Intl.DateTimeFormat(lang(), Object.assign({ timeZone: 'UTC' }, opts)).format(new Date(Date.UTC(y, m, d)));
        } catch {
            return '';
        }
    };

    function dayOk(v: Ymd): boolean {
        const s = isoDay(v);
        const lo = input ? input.min.slice(0, 10) : '';
        const hi = input ? input.max.slice(0, 10) : '';
        return (!lo || s >= lo) && (!hi || s <= hi);
    }

    function monthOk(y: number, m: number): boolean {
        const s = isoMonth(y, m);
        const lo = input ? input.min.slice(0, 7) : '';
        const hi = input ? input.max.slice(0, 7) : '';
        return (!lo || s >= lo) && (!hi || s <= hi);
    }

    function yearOk(y: number): boolean {
        const lo = input ? parseDay(input.min) : null;
        const hi = input ? parseDay(input.max) : null;
        return (!lo || y >= lo.y) && (!hi || y <= hi.y);
    }

    function render(place = true) {
        if (!panel || !input) {
            return;
        }
        panel.innerHTML = '';
        const body = el(doc, 'div', 'ph-dp__body');
        if (kind !== 'time') {
            body.appendChild(calendar());
        }
        if (kind === 'time' || kind === 'datetime-local') {
            body.appendChild(clock());
        }
        panel.appendChild(body);
        panel.appendChild(footer());
        if (place && popup) {
            popup.place();
            afterRender();
        }
    }

    function afterRender() {
        if (!panel) {
            return;
        }
        // Each time column opens with its chosen value in the middle.
        panel.querySelectorAll('.ph-dp__col').forEach(col => {
            const on = col.querySelector('.is-selected') as HTMLElement;
            const c = col as HTMLElement;
            if (on) {
                c.scrollTop = on.offsetTop - (c.clientHeight - on.offsetHeight) / 2;
            }
        });
        if (keyFocus) {
            const target = panel.querySelector('.ph-dp__grid [tabindex="0"]') as HTMLElement
                || panel.querySelector('.ph-dp__col .is-selected, .ph-dp__col button') as HTMLElement;
            if (target) {
                target.focus({ preventScroll: true });
            }
        }
    }

    function button(className: string, html: string, action: string, label?: string): HTMLButtonElement {
        const b = el(doc, 'button', className);
        b.type = 'button';
        b.tabIndex = -1;
        b.innerHTML = html;
        b.dataset.act = action;
        if (label) {
            b.setAttribute('aria-label', label);
            b.title = label;
        }
        return b;
    }

    function calendar(): HTMLElement {
        const cal = el(doc, 'div', 'ph-dp__cal');
        const head = el(doc, 'div', 'ph-dp__head');
        let title: string;
        if (view === 'days') {
            title = fmt({ month: 'long', year: 'numeric' }, cursor.y, cursor.m);
        } else if (view === 'months') {
            title = num(cursor.y);
        } else {
            title = num(yearsFrom) + ' – ' + num(yearsFrom + YEARS_PAGE - 1);
        }
        const up = button('ph-dp__title', '', view === 'years' ? 'view-back' : 'view-up', t('picker-choose-year', 'Choose the year'));
        up.appendChild(el(doc, 'span', '', title));
        const caret = el(doc, 'span', 'ph-dp__caret' + (view !== 'days' ? ' is-up' : ''));
        caret.innerHTML = ICONS.caret;
        up.appendChild(caret);
        up.tabIndex = 0;
        head.appendChild(up);
        const nav = el(doc, 'div', 'ph-dp__nav');
        const prev = button('ph-dp__arrow', ICONS.left, 'prev', t('picker-previous', 'Previous'));
        const next = button('ph-dp__arrow', ICONS.right, 'next', t('picker-next', 'Next'));
        prev.tabIndex = next.tabIndex = 0;
        nav.appendChild(prev);
        nav.appendChild(next);
        head.appendChild(nav);
        cal.appendChild(head);

        if (view === 'days') {
            cal.appendChild(days());
        } else if (view === 'months') {
            cal.appendChild(months());
        } else {
            cal.appendChild(years());
        }
        return cal;
    }

    function days(): HTMLElement {
        const wrap = el(doc, 'div', 'ph-dp__days');
        const first = firstDayOf(lang());
        const week = el(doc, 'div', 'ph-dp__week');
        for (let i = 0; i < 7; i++) {
            const wd = (first + i) % 7;
            // 2023-01-01 was a Sunday.
            const name = fmt({ weekday: 'narrow' }, 2023, 0, 1 + wd);
            const cell = el(doc, 'span', '', name);
            cell.title = fmt({ weekday: 'long' }, 2023, 0, 1 + wd);
            week.appendChild(cell);
        }
        wrap.appendChild(week);

        const grid = el(doc, 'div', 'ph-dp__grid ph-dp__grid--days');
        grid.setAttribute('role', 'grid');
        const lead = (new Date(Date.UTC(cursor.y, cursor.m, 1)).getUTCDay() - first + 7) % 7;
        for (let i = 0; i < lead; i++) {
            grid.appendChild(el(doc, 'span', 'ph-dp__blank'));
        }
        const now = isoDay(today(win));
        const chosen = input ? input.value.slice(0, 10) : '';
        const n = daysIn(cursor.y, cursor.m);
        for (let d = 1; d <= n; d++) {
            const v = { y: cursor.y, m: cursor.m, d };
            const s = isoDay(v);
            const b = button('ph-dp__day', num(d), 'day');
            b.dataset.v = s;
            b.setAttribute('role', 'gridcell');
            b.setAttribute('aria-label', fmt({ dateStyle: 'full' } as any, cursor.y, cursor.m, d));
            if (s === now) {
                b.classList.add('is-today');
                b.setAttribute('aria-current', 'date');
            }
            if (s === chosen) {
                b.classList.add('is-selected');
                b.setAttribute('aria-selected', 'true');
            }
            if (!dayOk(v)) {
                b.disabled = true;
            }
            if (d === cursor.d) {
                b.tabIndex = 0;
            }
            grid.appendChild(b);
        }
        wrap.appendChild(grid);
        return wrap;
    }

    function months(): HTMLElement {
        const grid = el(doc, 'div', 'ph-dp__grid ph-dp__grid--months');
        const chosen = input ? input.value.slice(0, 7) : '';
        const now = today(win);
        for (let m = 0; m < 12; m++) {
            const b = button('ph-dp__month', fmt({ month: 'short' }, cursor.y, m), 'month');
            b.dataset.v = String(m);
            b.setAttribute('aria-label', fmt({ month: 'long', year: 'numeric' }, cursor.y, m));
            if (isoMonth(cursor.y, m) === chosen) {
                b.classList.add('is-selected');
            }
            if (cursor.y === now.y && m === now.m) {
                b.classList.add('is-today');
            }
            if (!monthOk(cursor.y, m)) {
                b.disabled = true;
            }
            if (m === cursor.m) {
                b.tabIndex = 0;
            }
            grid.appendChild(b);
        }
        return grid;
    }

    function years(): HTMLElement {
        const grid = el(doc, 'div', 'ph-dp__grid ph-dp__grid--years');
        const chosen = input ? parseDay(input.value) : null;
        const now = today(win);
        for (let y = yearsFrom; y < yearsFrom + YEARS_PAGE; y++) {
            const b = button('ph-dp__month', num(y), 'year');
            b.dataset.v = String(y);
            if (chosen && chosen.y === y) {
                b.classList.add('is-selected');
            }
            if (y === now.y) {
                b.classList.add('is-today');
            }
            if (!yearOk(y)) {
                b.disabled = true;
            }
            if (y === cursor.y) {
                b.tabIndex = 0;
            }
            grid.appendChild(b);
        }
        return grid;
    }

    function minuteStep(): number {
        const s = input ? parseFloat(input.step) : NaN;
        if (!s || isNaN(s) || s < 60) {
            return 1;
        }
        const m = Math.round(s / 60);
        return m >= 1 && m <= 30 && 60 % m === 0 ? m : 1;
    }

    function clock(): HTMLElement {
        const wrap = el(doc, 'div', 'ph-dp__clock');
        const time = input ? parseTime(input.value) : null;
        const cols: Array<[string, string, number, number]> = [
            [t('picker-hour', 'Hour'), 'hour', 24, 1],
            [t('picker-minute', 'Min'), 'minute', 60, minuteStep()],
        ];
        for (const [label, act, count, step] of cols) {
            const part = el(doc, 'div', 'ph-dp__part');
            part.appendChild(el(doc, 'div', 'ph-dp__part-head', label));
            const col = el(doc, 'div', 'ph-dp__col');
            col.setAttribute('role', 'listbox');
            col.setAttribute('aria-label', label);
            const chosen = time ? (act === 'hour' ? time.h : time.mi) : -1;
            for (let v = 0; v < count; v += step) {
                const b = button('ph-dp__cell', num(v, 2), act);
                b.dataset.v = String(v);
                b.setAttribute('role', 'option');
                if (v === chosen) {
                    b.classList.add('is-selected');
                    b.setAttribute('aria-selected', 'true');
                }
                col.appendChild(b);
            }
            part.appendChild(col);
            wrap.appendChild(part);
        }
        return wrap;
    }

    function footer(): HTMLElement {
        const foot = el(doc, 'div', 'ph-dp__foot');
        if (input && !input.required) {
            foot.appendChild(button('ph-dp__text', t('picker-clear', 'Clear'), 'clear'));
        }
        foot.appendChild(el(doc, 'span', 'ph-dp__spacer'));
        const nowLabel = kind === 'date' ? t('picker-today', 'Today')
            : kind === 'month' ? t('picker-this-month', 'This month') : t('picker-now', 'Now');
        const nowButton = button('ph-dp__text', nowLabel, 'now');
        const d = today(win);
        nowButton.disabled = kind === 'month' ? !monthOk(d.y, d.m) : kind !== 'time' && !dayOk(d);
        foot.appendChild(nowButton);
        if (kind === 'time' || kind === 'datetime-local') {
            foot.appendChild(button('ph-dp__done', t('picker-done', 'Done'), 'done'));
        }
        foot.querySelectorAll('button').forEach(b => (b as HTMLButtonElement).tabIndex = 0);
        return foot;
    }

    function write(v: string) {
        const i = input;
        if (!i) {
            return;
        }
        writing = true;
        try {
            commit(i, () => i.value = v);
        } finally {
            writing = false;
        }
    }

    function finish() {
        const i = input;
        closePopup();
        if (i) {
            i.focus({ preventScroll: true });
        }
    }

    function nowTime(): Hm {
        const d = siteClock();
        const step = minuteStep();
        return { h: d.getUTCHours(), mi: Math.floor(d.getUTCMinutes() / step) * step };
    }

    function pickDay(v: Ymd) {
        if (!input || !dayOk(v)) {
            return;
        }
        cursor = v;
        if (kind === 'date') {
            write(isoDay(v));
            finish();
            return;
        }
        const time = parseTime(input.value) || nowTime();
        write(isoDay(v) + 'T' + pad(time.h) + ':' + pad(time.mi));
        render();
    }

    function pickTime(part: 'hour' | 'minute', n: number) {
        if (!input) {
            return;
        }
        const time = parseTime(input.value) || { h: part === 'hour' ? n : nowTime().h, mi: 0 };
        if (part === 'hour') {
            time.h = n;
        } else {
            time.mi = n;
        }
        const hm = pad(time.h) + ':' + pad(time.mi);
        const hadDay = kind === 'time' || !!parseDay(input.value);
        if (kind === 'time') {
            write(hm);
        } else {
            const day = parseDay(input.value) || clampToRange(today(win));
            write(isoDay(day) + 'T' + hm);
            cursor = day;
        }
        if (hadDay) {
            markClock(time);
        } else {
            render();
        }
    }

    // A time pick moves the marks where they are: the columns stay put under the pointer.
    function markClock(time: Hm) {
        if (!panel) {
            return;
        }
        panel.querySelectorAll('.ph-dp__cell').forEach(n => {
            const b = n as HTMLButtonElement;
            const on = +(b.dataset.v || -1) === (b.dataset.act === 'hour' ? time.h : time.mi);
            b.classList.toggle('is-selected', on);
            if (on) {
                b.setAttribute('aria-selected', 'true');
            } else {
                b.removeAttribute('aria-selected');
            }
        });
    }

    function onPanelClick(e: Event) {
        const b = (e.target as Element).closest('button') as HTMLButtonElement;
        if (!b || b.disabled || !input) {
            return;
        }
        const v = b.dataset.v || '';
        switch (b.dataset.act) {
            case 'day': {
                const d = parseDay(v);
                if (d) {
                    pickDay(d);
                }
                return;
            }
            case 'month':
                if (kind === 'month') {
                    write(isoMonth(cursor.y, +v));
                    finish();
                    return;
                }
                cursor = { y: cursor.y, m: +v, d: Math.min(cursor.d, daysIn(cursor.y, +v)) };
                view = 'days';
                break;
            case 'year':
                cursor = { y: +v, m: cursor.m, d: Math.min(cursor.d, daysIn(+v, cursor.m)) };
                view = 'months';
                break;
            case 'view-up':
                view = view === 'days' ? 'months' : 'years';
                yearsFrom = cursor.y - (cursor.y % YEARS_PAGE);
                break;
            case 'view-back':
                view = kind === 'month' ? 'months' : 'days';
                break;
            case 'prev':
            case 'next': {
                const dir = b.dataset.act === 'prev' ? -1 : 1;
                if (view === 'days') {
                    cursor = addMonths(cursor, dir);
                } else if (view === 'months') {
                    cursor = addMonths(cursor, dir * 12);
                } else {
                    yearsFrom += dir * YEARS_PAGE;
                }
                break;
            }
            case 'hour':
            case 'minute':
                pickTime(b.dataset.act, +v);
                return;
            case 'clear':
                write('');
                finish();
                return;
            case 'now': {
                const d = today(win);
                if (kind === 'date') {
                    write(isoDay(d));
                } else if (kind === 'month') {
                    write(isoMonth(d.y, d.m));
                } else {
                    const hm = nowTime();
                    write((kind === 'time' ? '' : isoDay(d) + 'T') + pad(hm.h) + ':' + pad(hm.mi));
                }
                finish();
                return;
            }
            case 'done':
                finish();
                return;
        }
        render();
    }

    function onPanelKey(e: KeyboardEvent) {
        const target = e.target as HTMLElement;
        const inGrid = !!target.closest('.ph-dp__grid');
        const inCol = target.closest('.ph-dp__col') as HTMLElement;
        const k = e.key;
        if (inCol && (k === 'ArrowDown' || k === 'ArrowUp')) {
            e.preventDefault();
            const sib = (k === 'ArrowDown' ? target.nextElementSibling : target.previousElementSibling) as HTMLElement;
            if (sib) {
                sib.focus();
                sib.scrollIntoView({ block: 'nearest' });
            }
            return;
        }
        if (!inGrid) {
            return;
        }
        let handled = true;
        if (view === 'days') {
            switch (k) {
                case 'ArrowLeft': cursor = addDays(cursor, isRtl() ? 1 : -1); break;
                case 'ArrowRight': cursor = addDays(cursor, isRtl() ? -1 : 1); break;
                case 'ArrowUp': cursor = addDays(cursor, -7); break;
                case 'ArrowDown': cursor = addDays(cursor, 7); break;
                case 'PageUp': cursor = addMonths(cursor, e.shiftKey ? -12 : -1); break;
                case 'PageDown': cursor = addMonths(cursor, e.shiftKey ? 12 : 1); break;
                case 'Home': cursor = addDays(cursor, -((new Date(Date.UTC(cursor.y, cursor.m, cursor.d)).getUTCDay() - firstDayOf(lang()) + 7) % 7)); break;
                case 'End': cursor = addDays(cursor, 6 - ((new Date(Date.UTC(cursor.y, cursor.m, cursor.d)).getUTCDay() - firstDayOf(lang()) + 7) % 7)); break;
                case 'Enter':
                case ' ': pickDay(cursor); e.preventDefault(); return;
                default: handled = false;
            }
        } else if (view === 'months') {
            const step: { [k: string]: number } = { ArrowLeft: isRtl() ? 1 : -1, ArrowRight: isRtl() ? -1 : 1, ArrowUp: -3, ArrowDown: 3 };
            if (step[k] !== undefined) {
                cursor = addMonths(cursor, step[k]);
            } else if (k === 'Enter' || k === ' ') {
                target.click();
            } else {
                handled = false;
            }
        } else {
            const step: { [k: string]: number } = { ArrowLeft: isRtl() ? 1 : -1, ArrowRight: isRtl() ? -1 : 1, ArrowUp: -4, ArrowDown: 4 };
            if (step[k] !== undefined) {
                cursor = { y: cursor.y + step[k], m: cursor.m, d: Math.min(cursor.d, daysIn(cursor.y + step[k], cursor.m)) };
                if (cursor.y < yearsFrom || cursor.y >= yearsFrom + YEARS_PAGE) {
                    yearsFrom = cursor.y - (((cursor.y % YEARS_PAGE) + YEARS_PAGE) % YEARS_PAGE);
                }
            } else if (k === 'Enter' || k === ' ') {
                target.click();
            } else {
                handled = false;
            }
        }
        if (handled) {
            e.preventDefault();
            e.stopPropagation();
            keyFocus = true;
            render();
        }
    }

    function isRtl(): boolean {
        return !!input && win.getComputedStyle(input).direction === 'rtl';
    }
}
